import React, { useState } from 'react';
import { Modal, Form, Input, Button, message, Space, Tag, Divider, Spin, Select } from 'antd';
import { BarChartOutlined, MailOutlined, SendOutlined } from '@ant-design/icons';
import axios from 'axios';
import Cookies from 'js-cookie';
import config from '../config';
import './DashboardEmailModal.css';

export default function DashboardEmailModal({ 
  visible, 
  onClose, 
  dashboardData,
  darkTheme = false,
  defaultRecipients = []
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Do not preselect recipients by default; show them as options only.

  const handelSendEmail = async (values) => {
    setLoading(true);
    try {
      const { toEmail, subject } = values;
      // Normalize recipients to array of trimmed emails
      const recipients = Array.isArray(toEmail)
        ? toEmail.map(s => s.trim()).filter(Boolean)
        : (toEmail ? toEmail.split(',').map(s => s.trim()).filter(Boolean) : []);
      const token = Cookies.get('token');

      if (!recipients.length || !subject) {
        message.error('Please provide recipients and a subject');
        setLoading(false);
        return;
      }

      const useTestEndpoint = !token;
      if (useTestEndpoint) {
        message.info('No auth token found — using local test endpoint (no auth).');
      }

      // Determine endpoint and headers. If token missing, use unauthenticated test endpoint.
      const endpoint = useTestEndpoint ? `${config.apiBaseUrl}/api/email/send-test` : `${config.apiBaseUrl}/api/email/send-dashboard`;
      const headers = useTestEndpoint
        ? { 'Content-Type': 'application/json' }
        : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

      // Send email with dashboard data (backend will generate HTML)
      const response = await axios.post(
        endpoint,
        {
          to: recipients,
          subject: subject || 'Dashboard Report',
          dashboardData: dashboardData
        },
        { headers }
      );

      if (response.data.success) {
        message.success('📧 Email sent successfully!');
        form.resetFields();
        onClose();
      } else {
        message.error(response.data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Email error:', error);
      message.error(error.response?.data?.error || error.message || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      className={`dashboard-email-modal${darkTheme ? ' dashboard-email-modal-dark' : ''}`}
      title={
        <div className="dashboard-email-modal-title">
          <span className="dashboard-email-modal-title-icon"><MailOutlined /></span>
          <span>Send Dashboard Report via Email</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={620}
    >
      <Spin spinning={loading}>
        <div className="dashboard-email-modal-content">
          {/* Dashboard Preview */}
          <div className="dashboard-email-preview">
            <div className="dashboard-email-section-title">
              <BarChartOutlined />
              <span>Report Preview</span>
            </div>
            <div className="dashboard-email-kpi-grid">
              <div className="dashboard-email-kpi dashboard-email-kpi-scheduled">
                <p>Scheduled Today</p>
                <span>
                  {dashboardData?.scheduledForPackingToday || 0}
                </span>
              </div>
              <div className="dashboard-email-kpi dashboard-email-kpi-picked">
                <p>Today's Picked</p>
                <span>
                  {dashboardData?.todayPickedCount || 0}
                </span>
              </div>
              <div className="dashboard-email-kpi dashboard-email-kpi-completed">
                <p>Completed</p>
                <span>
                  {dashboardData?.todayStats?.todayCompletedCount || 0}
                </span>
              </div>
              <div className="dashboard-email-kpi dashboard-email-kpi-pending">
                <p>Pending</p>
                <span>
                  {dashboardData?.todayNotCompletedCount || 0}
                </span>
              </div>
            </div>
          </div>

          <Divider className="dashboard-email-divider" />

          {/* Email Form */}
          <Form form={form} layout="vertical" onFinish={handelSendEmail}>
            <Form.Item
              label="Recipient Email(s)"
              name="toEmail"
              rules={[
                { required: true, message: 'Please enter at least one valid email' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const emails = Array.isArray(value) ? value : (value ? value.split(',').map(s=>s.trim()) : []);
                    if (!emails.length) {
                      return Promise.reject(new Error('Please enter at least one recipient'));
                    }
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const invalid = emails.find(e => !emailRegex.test(e));
                    if (invalid) {
                      return Promise.reject(new Error(`${invalid} is not a valid email`));
                    }
                    return Promise.resolve();
                  }
                })
              ]}
            >
              <Select
                mode="tags"
                className="dashboard-email-input"
                placeholder="Add or paste comma-separated emails"
                tokenSeparators={[',']}
                options={(defaultRecipients || []).map(r => ({ value: r, label: r }))}
                popupClassName="dashboard-email-select-dropdown"
              />
            </Form.Item>

            <Form.Item
              label="Email Subject"
              name="subject"
              initialValue="Dashboard Report"
              rules={[{ required: true, message: 'Please enter email subject' }]}
            >
              <Input
                className="dashboard-email-input"
                placeholder="Dashboard Report"
              />
            </Form.Item>

            <Space className="dashboard-email-modal-footer">
              <Button className="dashboard-email-cancel" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SendOutlined />}
                className="dashboard-email-send"
              >
                Send Email
              </Button>
            </Space>
          </Form>
        </div>
      </Spin>
    </Modal>
  );
}
